import { EllipsisOutlined } from '@ant-design/icons';
import React from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { CenterFlex } from '@/components/base';
import IconButton from '@/components/Button/IconButton';
import Menu from '@/components/Menu/Menu';
import { MainText } from '@/components/Typography/NamedTypo';
import { DashboardFeedbackGallery } from '@/features/(dashboard)/feedback-gallery';
import { Dashboard2ColumnType } from '@/features/Dashboard/types/constants';
import type { MappedChartColumn } from '@/features/Dashboard/types/types';
import service from '@/services';

import DashboardAnalysisBox from './DashboardCardContent/DashboardAnalysisBox';
import DashboardChartContent from './DashboardCardContent/DashboardChartContent';
import DashboardTextBox from './DashboardCardContent/DashboardTextBox';
import DashboardCardStyles from './styles/DashboardCardStyles';

const { DashboardCardHeader, StyledDashboardCard, StyledDashboardCardWrapper } =
  DashboardCardStyles;

export type DashboardCardProps = {
  index: number;
  rowIndex: number;
  dashboardId: string;
  rowId: string;
  column: MappedChartColumn;
  someDragging: boolean;
  setSomeDragging: (someDragging: boolean) => void;
  width: string;
};

const Fallback = (props: DashboardCardProps) => {
  return (
    <StyledDashboardCardWrapper isDragging={false} width={props.width}>
      <StyledDashboardCard>
        <DashboardCardHeader j="flex-end" a="center">
          <Menu
            triggerNode={
              <IconButton
                sizeType="m"
                icon={<EllipsisOutlined />}
                outlined={false}
              />
            }
            options={[
              {
                label: 'Delete',
                onClick: async () => {
                  await service.dashboard2.deleteColumnInDashboardRow(
                    props.dashboardId,
                    props.rowId,
                    props.column.id,
                  );
                },
              },
            ]}
          />
        </DashboardCardHeader>
        <CenterFlex f={1}>
          <MainText dim>Error occurred</MainText>
        </CenterFlex>
      </StyledDashboardCard>
    </StyledDashboardCardWrapper>
  );
};

const DashboardCard = React.memo((props: DashboardCardProps) => {
  if (props.column.type === Dashboard2ColumnType.DASHBOARD_COLUMN_TEXT_BOX)
    return <DashboardTextBox {...props} />;

  if (props.column.type === Dashboard2ColumnType.DASHBOARD_COLUMN_ANALYSIS)
    return (
      <ErrorBoundary fallback={<Fallback {...props} />}>
        <DashboardAnalysisBox {...props} />
      </ErrorBoundary>
    );

  if (props.column.type === Dashboard2ColumnType.DASHBOARD_COLUMN_CHART)
    return (
      <ErrorBoundary fallback={<Fallback {...props} />}>
        <DashboardChartContent {...props} />
      </ErrorBoundary>
    );
  if (props.column.type === Dashboard2ColumnType.DASHBOARD_COLUMN_FEEDBACK_GALLERY)
    return (
      <ErrorBoundary fallback={<Fallback {...props} />}>
        <DashboardFeedbackGallery {...props} />
      </ErrorBoundary>
    );

});

export default DashboardCard;
